import {
  Activity,
  ChartColumn,
  Database,
  FileText,
  Flag,
  Inbox,
  KeyRound,
  LayoutDashboard,
  Merge,
  Package,
  ScrollText,
  Settings2,
  ShieldCheck,
  Smartphone,
  ToggleRight,
  Users,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';

import { createAdminApi, roleSatisfies, type AdminRole } from '@/admin/adminApi';
import { ADMINS_PATH } from '@/admin/admins/routes';
import { AUDIT_PATH } from '@/admin/audit/auditRoutes';
import { CONFLICTS_PATH } from '@/admin/conflicts/conflictRoutes';
import { CONTENT_PATH } from '@/admin/content/routes';
import { DATA_PATH } from '@/admin/data/routes';
import { FAMILIES_PATH } from '@/admin/families/routes';
import { MODERATION_PATH } from '@/admin/moderation/routes';
import { FAILED_MESSAGES_PATH, HEALTH_PATH } from '@/admin/operations/routes';
import { STATISTICS_PATH } from '@/admin/pilotage/routes';
import { PRIVACY_REQUESTS_PATH } from '@/admin/privacy-requests/routes';
import { STOCK_PATH } from '@/admin/stock/routes';
import { ACCOUNTS_USERS_PATH } from '@/admin/users/routes';
import { SYNC_SUPERVISION_PATH } from '@/admin/synchronization/syncSupervisionApi';
import type { HttpClient } from '@/api/httpClient';
import { RESOURCE_CATALOG } from '@/resources/catalog';

/**
 * Registre de navigation : six rubriques (ADR 0035 §2, maquette
 * B1 « Coque »).
 *
 * **Une entree sans cible n'est jamais rendue**, et une rubrique sans entree
 * rendue disparait. C'est le renversement de la regle precedente, qui affichait
 * grisees et « a venir » les rubriques non servies : elle annoncait trois
 * rubriques dont les routes existaient deja, et donnait au menu la forme d'une
 * promesse plutot que d'un outil. Le registre decrit donc la cible entiere —
 * libelle, pictogramme, place — et **chaque ecran livre ajoute sa cible en une
 * ligne** (`target: route(...)` sur son entree) : rien d'autre a toucher, ni
 * dans la barre laterale, ni dans le fil d'Ariane.
 *
 * L'ordre est celui de la maquette (`design/figma-backoffice/proposition/ui.js`,
 * tableau `NAV`), « Alertes » en moins : la carte « À traiter » du tableau de
 * bord la remplace.
 *
 * Module **pur** : aucun composant, aucun acces reseau. Les pictogrammes sont des
 * references (des fonctions), pas des elements rendus. C'est ce qui permet de
 * tester l'organisation du menu sans monter React Admin.
 */

/** Chemin de la page de diagnostic technique, ouverte depuis le menu utilisateur. */
export const DIAGNOSTIC_PATH = '/diagnostic';

/** Chemin des reglages globaux d'exploitation. */
export const GLOBAL_SETTINGS_PATH = '/settings';

export type NavSectionId = 'pilotage' | 'users' | 'data' | 'compliance' | 'operations' | 'settings';

export const NAV_SECTIONS: readonly { readonly id: NavSectionId; readonly label: string }[] = [
  { id: 'pilotage', label: 'Pilotage' },
  { id: 'users', label: 'Utilisateurs' },
  { id: 'data', label: 'Données' },
  { id: 'compliance', label: 'Conformité' },
  { id: 'operations', label: 'Exploitation' },
  { id: 'settings', label: 'Réglages' },
];

/**
 * Destination d'une entree. Une ressource React Admin se liste sous
 * `/<nom>` ; une route est un ecran declare dans `App.tsx`.
 */
export type NavTarget =
  | { readonly kind: 'route'; readonly path: string }
  | { readonly kind: 'resource'; readonly resource: string };

const route = (path: string): NavTarget => ({ kind: 'route', path });

/**
 * Lecture d'une pastille chiffree.
 *
 * La valeur doit etre **un nombre rendu par le serveur** : la pastille est lue
 * une fois au montage de la coque, puis sur demande de l'ecran dont l'action la
 * change (`useNavBadges().refresh`), **jamais sur minuterie** — chaque appel a
 * `/admin/*` ecrit une ligne d'audit (ADR 0035 §4). Une lecture en echec, ou qui
 * ne rend pas un entier positif ou nul, omet la pastille : un zero affiche sur
 * une erreur dirait « rien a traiter » au moment ou on ne le sait pas.
 */
export type NavBadgeSource = (http: HttpClient) => Promise<unknown>;

export interface NavEntry {
  readonly id: string;
  readonly section: NavSectionId;
  readonly label: string;
  readonly icon: LucideIcon;
  /** Absente : l'ecran n'est pas livre, l'entree n'est pas rendue. */
  readonly target?: NavTarget;
  /**
   * Role minimal pour voir l'entree. **Confort seulement** : le serveur reste
   * l'autorite, et une entree masquee n'est pas une route protegee.
   */
  readonly minimumRole?: AdminRole;
  readonly badge?: NavBadgeSource;
  /**
   * Chemins supplementaires qui rendent l'entree courante (fil d'Ariane,
   * `aria-current`), quand l'ecran en ouvre d'autres que sa cible.
   */
  readonly activePaths?: readonly string[];
}

/**
 * Collections du catalogue rangees sous l'explorateur. Transitoire : ces chemins
 * disparaissent avec le catalogue (ADR 0035, Consequences). Les documents legaux
 * en sont sortis : l'ecran « Contenus » les remplace.
 */
const CATALOG_PATHS_UNDER_EXPLORER: readonly string[] = RESOURCE_CATALOG.map((descriptor) => `/${descriptor.name}`);

export const NAV_ENTRIES: readonly NavEntry[] = [
  { id: 'dashboard', section: 'pilotage', label: 'Tableau de bord', icon: LayoutDashboard, target: route('/') },
  { id: 'stats', section: 'pilotage', label: 'Statistiques', icon: ChartColumn, target: route(STATISTICS_PATH), minimumRole: 'support' },
  { id: 'accounts', section: 'users', label: 'Comptes', icon: Users, target: route(ACCOUNTS_USERS_PATH) },
  { id: 'families', section: 'users', label: 'Familles et partages', icon: UsersRound, target: route(FAMILIES_PATH) },
  { id: 'moderation', section: 'users', label: 'Modération', icon: Flag, target: route(MODERATION_PATH) },
  { id: 'explorer', section: 'data', label: 'Explorateur de données', icon: Database, target: route(DATA_PATH), activePaths: CATALOG_PATHS_UNDER_EXPLORER },
  { id: 'stock', section: 'data', label: 'Stock et traçabilité', icon: Package, target: route(STOCK_PATH) },
  { id: 'content', section: 'data', label: 'Contenus', icon: FileText, target: route(CONTENT_PATH) },
  {
    id: 'gdpr',
    section: 'compliance',
    label: 'Demandes RGPD',
    icon: ShieldCheck,
    target: route(PRIVACY_REQUESTS_PATH),
    minimumRole: 'support',
    // Le nombre de demandes ouvertes, rendu par le serveur (`GET /summary`), jamais deduit de la liste.
    badge: async (http) => (await createAdminApi(http).privacyRequestsSummary()).open,
  },
  { id: 'audit', section: 'compliance', label: 'Journal d’audit', icon: ScrollText, target: route(AUDIT_PATH) },
  { id: 'health', section: 'operations', label: 'Santé du service', icon: Activity, target: route(HEALTH_PATH), minimumRole: 'support' },
  { id: 'synchronizations', section: 'operations', label: 'Synchronisations', icon: Activity, target: route(SYNC_SUPERVISION_PATH), minimumRole: 'support' },
  { id: 'conflicts', section: 'operations', label: 'Conflits', icon: Merge, target: route(CONFLICTS_PATH), minimumRole: 'support' },
  {
    id: 'jobs',
    section: 'operations',
    label: 'Traitements en erreur',
    icon: Inbox,
    target: route(FAILED_MESSAGES_PATH),
    minimumRole: 'support',
    // Le `total` reel du transport d'echec (une page d'une ligne suffit), jamais le
    // `detail` de la sonde de sante, qui est une phrase.
    badge: async (http) => (await createAdminApi(http).listFailedMessages(1, 1)).total,
  },
  { id: 'releases', section: 'operations', label: 'Versions de l’app', icon: Smartphone },
  { id: 'global-settings', section: 'settings', label: 'Réglages globaux', icon: Settings2, target: route(GLOBAL_SETTINGS_PATH) },
  { id: 'flags', section: 'settings', label: 'Fonctionnalités', icon: ToggleRight },
  { id: 'admins', section: 'settings', label: 'Administrateurs', icon: KeyRound, target: route(ADMINS_PATH) },
];

/** Entree rendue : sa cible est garantie par le type. */
export type RenderedNavEntry = NavEntry & { readonly target: NavTarget };

export interface RenderedNavSection {
  readonly id: NavSectionId;
  readonly label: string;
  readonly entries: readonly RenderedNavEntry[];
}

const hasTarget = (entry: NavEntry): entry is RenderedNavEntry => entry.target !== undefined;

/**
 * Ce que la barre laterale rend.
 *
 * @param role role lu sur `/admin/me` ; `null` ou inconnu masque les entrees qui
 *             exigent un role minimal (confort, jamais securite)
 */
export const visibleSections = (
  role: AdminRole | null,
  entries: readonly NavEntry[] = NAV_ENTRIES,
): readonly RenderedNavSection[] =>
  NAV_SECTIONS.map((section) => ({
    ...section,
    entries: entries
      .filter((entry) => entry.section === section.id)
      .filter(hasTarget)
      .filter((entry) => entry.minimumRole === undefined || roleSatisfies(role, entry.minimumRole)),
  })).filter((section) => section.entries.length > 0);

/** Chemin de l'application vers lequel mene une cible. */
export const targetPath = (target: NavTarget): string =>
  target.kind === 'route' ? target.path : `/${target.resource}`;

/** `/` ne couvre que l'accueil ; tout autre chemin couvre aussi ses sous-chemins. */
const covers = (base: string, pathname: string): boolean =>
  base === '/' ? pathname === '/' : pathname === base || pathname.startsWith(`${base}/`);

export interface ActiveNavEntry {
  readonly section: { readonly id: NavSectionId; readonly label: string };
  readonly entry: RenderedNavEntry;
  /** Vrai quand la page est la cible meme de l'entree, et non une page en dessous. */
  readonly exact: boolean;
}

/**
 * Entree courante pour un chemin : celle dont la cible (ou un chemin
 * supplementaire) couvre le chemin le plus long. `null` hors navigation
 * (diagnostic, page introuvable).
 */
export const activeEntryFor = (
  pathname: string,
  sections: readonly RenderedNavSection[],
): ActiveNavEntry | null => {
  let best: { found: ActiveNavEntry; length: number } | null = null;

  for (const section of sections) {
    for (const entry of section.entries) {
      for (const base of [targetPath(entry.target), ...(entry.activePaths ?? [])]) {
        if (covers(base, pathname) && (best === null || base.length > best.length)) {
          best = {
            found: { section: { id: section.id, label: section.label }, entry, exact: pathname === targetPath(entry.target) },
            length: base.length,
          };
        }
      }
    }
  }

  return best?.found ?? null;
};

/** Une pastille ne montre qu'un entier positif ou nul ; tout le reste est omis. */
export const badgeCount = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
